import Head from "next/head";
import { useEffect, useRef, useState } from "react";
import { ErrorPage } from "../../components/ErrorPage";
import {
  SandboxPreview,
  SandboxConfig,
  SandboxSkeleton,
} from "../../features/sandbox";
import {
  SandboxProvider,
  useSandbox,
} from "../../features/sandbox/SandboxProvider";

const SandboxContent = () => {
  const [hideEmbed, setHideEmbed] = useState(false);
  const [response, setResponse] = useState<Record<string, any>>({});
  const isInitialLoad = useRef(true);

  const { isLoading, error } = useSandbox();

  useAppCuesTrackSuccessfulPurchaseOnTestMode();

  if (isLoading && isInitialLoad.current) {
    isInitialLoad.current = false;
    return <SandboxSkeleton />;
  }

  if (!!error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <div className="rounded-lg border border-foundations-subtle bg-foundations-default p-4 lg:w-0 lg:flex-[2]">
        <SandboxConfig
          onHideEmbedChange={setHideEmbed}
          onResponseChange={setResponse}
        />
      </div>

      <div className="flex h-[900px] flex-col lg:h-auto lg:flex-[3]">
        <SandboxPreview
          hideEmbed={hideEmbed}
          embedUrl={response?.data?.embed_url || ""}
          response={response}
          onResponseChange={setResponse}
        />
      </div>
    </>
  );
};
export default function Sandbox() {
  return (
    <>
      <Head>
        <title>Sandbox - {process.env.NEXT_PUBLIC_APP_TITLE}</title>
      </Head>
      <main className="flex-1 overflow-y-auto bg-neutral-50 p-8 ">
        <div className="mx-auto flex min-h-full flex-col gap-4 lg:max-w-screen-2xl lg:flex-row">
          <SandboxProvider>
            <SandboxContent />
          </SandboxProvider>
        </div>
      </main>
    </>
  );
}

Sandbox.hideTestModeBanner = true;

const useAppCuesTrackSuccessfulPurchaseOnTestMode = () => {
  const { isLive, isMHDemoKey, editor } = useSandbox();
  const editorValueRef = useRef(editor);
  editorValueRef.current = editor;

  useEffect(() => {
    const isTestMode = isMHDemoKey || !isLive;

    const handleMessage = (e: MessageEvent<any>) => {
      const { origin, data: eventData } = e;
      if (!origin.endsWith("moneyhash.io")) return;
      const { type, data } = eventData || {};
      if (
        type === "onComplete" &&
        data?.transaction?.status === "purchase.successful"
      ) {
        window.Appcues?.track(
          `transaction.purchase.successful${isTestMode ? "" : " (live)"}`
        );

        try {
          const flowId = JSON.parse(editorValueRef.current).flow_id;
          if (!flowId) return;
          window.Appcues?.track("sandbox_flow_test.successful");
        } catch (error) {}
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMHDemoKey, isLive]);
};
